'use client'
import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'
import Heading from '@/app/components/atoms/Heading'
import Media from '@/app/components/organisms/Media'
import Section from '@/app/components/sections/Section'
import Paragraph from '../atoms/Paragraph'
import Magnitize from '../Magnitize'
import Circle from '../atoms/Circle'

interface HeroProps {
  data?: any
}

const Hero: React.FC<HeroProps> = ({ data, ...props }) => {
  const headingRef = useRef<HTMLDivElement>(null)
  const imagesRef = useRef<HTMLDivElement>(null)
  const ParagraphRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Immediately set the element to its final position without any animation
    gsap.from(headingRef.current, {
      y: 50,
      opacity: 0,
      duration: 0.3,
      delay: 0.5,
      ease: 'power2.out',
    })
    gsap.to(headingRef.current, {
      y: 0,
      delay: 0.5,
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
    gsap.from(ParagraphRef.current, {
      y: 50,
      opacity: 0,
      duration: 0.3,
      delay: 0.8,
      ease: 'power2.out',
    })
    gsap.to(ParagraphRef.current, {
      y: 0,
      delay: 0.8,
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
    gsap.from(imagesRef.current, {
      opacity: 0,
      duration: 0.3,
      delay: 1,
      ease: 'power2.out',
    })
    gsap.to(imagesRef.current, {
      delay: 1,
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  }, [])

  // Classes to apply based on index
  /*   const mediaClasses = [
    'bottom-1/2 -right-1/2 xs:-right-[20%] sm:-right-[15%] sm:-bottom-[10%] scale-50 md:scale-150 lg:scale-[1.75]',
    ' sm:scale-75 -bottom-0 left-[40%] sm:left-[20%] md:left-[30%] xl:-bottom-[13%]',
    ' sm:scale-150 left-[20%] xs:-left-[10%] top-[40%] xs:top-[30%] md:left-0 md:top-[25%]',
  ] */

  const mediaClasses = [
    '-col-start-1 -col-end-6 row-start-11 -col-row-1 scale-[1.3] md:scale-100 size-[12rem] sm:size-[26rem] sm:row-start-9 lg:size-[42.25rem] xl:size-[39rem] lg:-col-start-1 lg:col-end-9 lg:-row-start-1 lg:row-end-7 lg:*:scale-[1.2] xl:col-end-10',
    'col-start-1 col-end-6 -row-start-1 -row-end-4 size-[12rem] xs:col-start-2 xs:-row-end-5 xs:size-[16rem] sm:col-start-1 sm:row-end-2 sm:col-start-1 sm:size-[23rem] lg:size-[18.75rem] lg:col-start-4 lg:col-end-7 lg:-row-start-1 lg:row-end-9 xl:row-end-9 xl:size-[22.75rem]',
    '-col-start-1 col-end-1 row-start-5 row-end-2 size-[19.75rem] xs:col-end-2 sm:-row-end-6 sm:-row-start-1 lg:size-[39rem] lg:col-start-1 lg:col-end-9 lg:row-start-2 lg:row-end-9 hidden sm:block',
  ]

  return (
    <>
      <Section
        {...props}
        className="h-screen/1.2 md:h-screen pt-40 md:pt-30 place-content-center -mt-12 prose-headings:text-rød relative "
        paddingTop="none"
        paddingBottom="none"
        paddingX="none"
      >
        <Section
          tag={'div'}
          paddingBottom="none"
          paddingX="none"
          paddingTop="none"
          className="col-span-full z-[100] bg-transparent pointer-events-none -mt-36 md:mt-0"
        >
          <div className="relative z-50 mx-auto my-auto text-center pointer-events-none col-span-full">
            <div className="pt-4 pb-8 overflow-hidden">
              <div ref={headingRef}>
                <Heading spacing="none" tag="h1" type="h1" fontFamily="heading" dangerouslySetInnerHTML={{ __html: data?.title }} />
              </div>
            </div>
            <div className="max-w-[64ch] mx-auto text-center ">
              <div ref={ParagraphRef}>
                <Paragraph spacing="none" size="increased">
                  {data?.description}
                </Paragraph>
              </div>
            </div>
          </div>
        </Section>

        <Section
          tag={'div'}
          paddingBottom="none"
          paddingTop="none"
          paddingX="left"
          className="absolute w-full h-full translate-x-1/2 -translate-y-1/2 col-span-full right-1/2 top-1/2 z-[99] "
        >
          <div
            ref={imagesRef}
            className="absolute grid w-full h-full grid-cols-12 col-span-full grid-rows-12 point"
          >
            {data?.medias?.map((media: any, index: number) => (
              <Magnitize
                key={index}
                intensity={3}
                className={` z-[100] absolute z-40 *:overflow-hidden p-4 lg:p-20 aspect-1 opacity-80  ${mediaClasses[index] || ''} *:before:size-full *:before:block *:before:absolute  *:before:bg-lys *:before:-z-10`}
              >
                <Media data={media.media} />
              </Magnitize>
            ))}
          </div>
        </Section>
        <div className="absolute top-0 right-0 w-full h-full overflow-hidden z-[99] pointer-events-none">
          <div className=" absolute bottom-0 -left-[15%] scale-[1.2] z-0 -rotate-12 border-4 border-DabraRød border-solid">
            <Circle
              theme="yellow"
              opacity={0}
              className="hidden w-full h-full xs:block"
            />
          </div>
        </div>
      </Section>
    </>
  )
}

export default Hero
